import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  ClickAwayListener,
  IconButton,
  Paper,
  Popper,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';
import LoadingSpinner from './LoadingSpinner';
import useCommonMediaQuery from '../../hooks/useCommonMediaQuery';

/**
 * Reusable month calendar grid (6 weeks, incl. prev/next-month spillover).
 *
 * Owns the view month + navigation and reports the visible ISO date range via
 * `onRangeChange(startDate, endDate)` so the parent can fetch data for exactly
 * what's shown. Entry rendering, legend and popover title are supplied by the
 * caller, keeping the shell feature-agnostic (leave, timesheet, …).
 *
 * Props:
 * - entriesByDate: { [isoDate]: entry[] } — items to render per day.
 * - loading:       show a spinner overlay over the grid.
 * - onRangeChange: (startDateISO, endDateISO) => void — fired on mount + nav.
 * - renderEntry:   (entry) => ReactNode — how to render one entry chip.
 * - getEntryKey:   (entry, index) => key.
 * - sortEntries:   optional comparator applied to each day's entries.
 * - legend:        [{ label, color }] shown top-right.
 * - maxVisibleEntries: cap before collapsing into "View More" (default 2).
 * - popoverTitle / popoverIcon: header for the day's full-list popover.
 * - cellHeight:    fixed day-cell height for a uniform grid (default 132).
 */

const WEEKDAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const GRID_CELLS = 42; // 6 weeks × 7 days

const viewMoreSx = {
  textTransform: 'none',
  fontSize: '0.7rem',
  fontWeight: 600,
  color: '#2F78EE',
  backgroundColor: '#F8FAFC',
  border: '1px solid #E4E5E7',
  borderRadius: '4px',
  py: 0.25,
  px: 0.5,
  // Fill the day cell instead of MUI's default 64px min-width (which spills
  // into the next cell on narrow mobile columns).
  minWidth: 0,
  width: '100%',
  minHeight: 0,
  lineHeight: 1.4,
  whiteSpace: 'nowrap',
  '&:hover': { backgroundColor: '#EEF2F6', borderColor: '#D1D5DB' },
};

const MonthCalendar = ({
  entriesByDate = {},
  loading = false,
  onRangeChange,
  renderEntry = () => null,
  getEntryKey = (entry, index) => entry?.id ?? index,
  sortEntries,
  legend = [],
  maxVisibleEntries = 2,
  popoverTitle = 'Details',
  popoverIcon = null,
  cellHeight = 132,
}) => {
  const isMobile = useCommonMediaQuery('sm');
  const [viewMonth, setViewMonth] = useState(() => dayjs().startOf('month'));

  const today = dayjs().format('YYYY-MM-DD');
  // Narrow mobile cells fit fewer entries; collapse sooner into "View More".
  const maxVisible = isMobile ? 1 : maxVisibleEntries;

  // Full visible grid (6 weeks incl. prev/next-month spillover days).
  const cells = useMemo(() => {
    const gridStart = viewMonth.startOf('month').startOf('week'); // Sunday
    return Array.from({ length: GRID_CELLS }, (_, i) => gridStart.add(i, 'day'));
  }, [viewMonth]);

  const startDate = cells[0].format('YYYY-MM-DD');
  const endDate = cells[cells.length - 1].format('YYYY-MM-DD');

  // Report the visible range so the parent fetches exactly what's shown.
  useEffect(() => {
    onRangeChange?.(startDate, endDate);
    // onRangeChange intentionally omitted — parent may pass an inline callback.
  }, [startDate, endDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const goToToday = () => setViewMonth(dayjs().startOf('month'));
  const goPrev = () => setViewMonth(prev => prev.subtract(1, 'month'));
  const goNext = () => setViewMonth(prev => prev.add(1, 'month'));

  // Day's full-list popover.
  const [popover, setPopover] = useState({ anchorEl: null, iso: null, entries: [] });
  const openDayPopover = (e, iso, entries) =>
    setPopover({ anchorEl: e.currentTarget, iso, entries });
  const closeDayPopover = () => setPopover(prev => ({ ...prev, anchorEl: null }));

  return (
    <Box sx={{ px: { xs: 0.75, sm: 3 }, py: { xs: 1.5, sm: 3 } }}>
      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          gap: 1.5,
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={goToToday}
            sx={{
              textTransform: 'none',
              color: '#374151',
              borderColor: '#D1D5DB',
              minWidth: 'auto',
              px: 1.5,
            }}
          >
            Today
          </Button>
          <IconButton size="small" onClick={goPrev} sx={{ border: '1px solid #E4E5E7' }}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={goNext} sx={{ border: '1px solid #E4E5E7' }}>
            <ChevronRightIcon fontSize="small" />
          </IconButton>
          <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#374151', ml: 1 }}>
            {viewMonth.format('MMMM YYYY')}
          </Typography>
        </Box>

        {legend.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            {legend.map(item => (
              <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: item.color }} />
                <Typography sx={{ fontSize: '0.75rem', color: '#6B7280' }}>{item.label}</Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Grid — fits the screen width on mobile (no horizontal scroll) */}
      <Box sx={{ position: 'relative', overflowX: 'auto' }}>
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255,255,255,0.6)',
            }}
          >
            <LoadingSpinner />
          </Box>
        )}
        <Box sx={{ minWidth: { xs: 0, sm: 640 }, width: '100%' }}>
          {/* Weekday header */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
              borderTop: '1px solid #E4E5E7',
              borderLeft: '1px solid #E4E5E7',
            }}
          >
            {WEEKDAYS.map(d => (
              <Box
                key={d}
                sx={{
                  py: 1,
                  px: { xs: 0.25, sm: 1 },
                  borderRight: '1px solid #E4E5E7',
                  borderBottom: '1px solid #E4E5E7',
                  backgroundColor: '#F9FAFB',
                  textAlign: { xs: 'center', sm: 'left' },
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: '0.6rem', sm: '0.7rem' },
                    fontWeight: 600,
                    color: '#9CA3AF',
                  }}
                >
                  {isMobile ? d.charAt(0) : d}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Day cells */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
              borderLeft: '1px solid #E4E5E7',
            }}
          >
            {cells.map(cell => {
              const iso = cell.format('YYYY-MM-DD');
              const inMonth = cell.month() === viewMonth.month();
              const isToday = iso === today;
              const raw = entriesByDate[iso] ?? [];
              const ordered = sortEntries ? [...raw].sort(sortEntries) : raw;
              // Show up to maxVisible; collapse the rest into View More.
              const hasOverflow = ordered.length > maxVisible;
              const visibleEntries = hasOverflow
                ? ordered.slice(0, maxVisible)
                : ordered;
              const moreCount = ordered.length - visibleEntries.length;
              return (
                <Box
                  key={iso}
                  // On mobile the cells are too narrow to show full details, so
                  // tapping any day with entries opens its full-list popover.
                  onClick={
                    isMobile && ordered.length
                      ? e => openDayPopover(e, iso, ordered)
                      : undefined
                  }
                  sx={{
                    // Grow to fit the day's entries (row equalizes to the
                    // tallest cell) so nothing is clipped.
                    minHeight: { xs: 108, sm: cellHeight },
                    p: { xs: 0.5, sm: 0.75 },
                    borderRight: '1px solid #E4E5E7',
                    borderBottom: '1px solid #E4E5E7',
                    backgroundColor: inMonth ? 'white' : '#FAFAFA',
                    cursor: isMobile && ordered.length ? 'pointer' : 'default',
                    ...(isToday && {
                      outline: '2px solid #2F78EE',
                      outlineOffset: '-2px',
                    }),
                  }}
                >
                  {/* Date number + TODAY pill */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 0.5,
                    }}
                  >
                    {isToday ? (
                      // Today's date sits inside a filled blue circle.
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          backgroundColor: '#2F78EE',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Typography
                          sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'white' }}
                        >
                          {cell.date()}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          color: inMonth ? '#374151' : '#C4C9D0',
                        }}
                      >
                        {cell.date()}
                      </Typography>
                    )}
                    {isToday && (
                      <Box sx={{ px: 0.75, py: 0.1, borderRadius: '8px', backgroundColor: '#2F78EE' }}>
                        <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, color: 'white' }}>
                          TODAY
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Entries + overflow */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {visibleEntries.map((entry, i) => (
                      <Box key={getEntryKey(entry, i)}>{renderEntry(entry)}</Box>
                    ))}
                    {hasOverflow && (
                      <Button
                        size="small"
                        onClick={e => {
                          e.stopPropagation();
                          openDayPopover(e, iso, ordered);
                        }}
                        sx={viewMoreSx}
                      >
                        {isMobile ? `+${moreCount}` : `View More (+${moreCount})`}
                      </Button>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* Full-list popover — stays attached to the day cell, no animation */}
      <Popper
        open={Boolean(popover.anchorEl)}
        anchorEl={popover.anchorEl}
        placement="bottom"
        modifiers={[
          { name: 'offset', options: { offset: [0, 8] } },
          { name: 'preventOverflow', options: { padding: 8 } },
          { name: 'flip', enabled: true },
        ]}
        sx={{ zIndex: 1300, transition: 'none !important' }}
      >
        <ClickAwayListener onClickAway={closeDayPopover}>
          <Paper
            sx={{
              borderRadius: 2,
              width: 300,
              maxWidth: '90vw',
              boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
              transition: 'none !important',
              animation: 'none !important',
            }}
          >
            <Box sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                {popoverIcon && (
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      backgroundColor: '#EEF2F6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {popoverIcon}
                  </Box>
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: '#111827' }}>
                    {popoverTitle}
                  </Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: '#6B7280' }}>
                    {popover.iso ? dayjs(popover.iso).format('dddd, MMMM DD, YYYY') : ''}
                  </Typography>
                </Box>
                <IconButton size="small" onClick={closeDayPopover} aria-label="Close">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.75,
                  maxHeight: 300,
                  overflowY: 'auto',
                  pr: 0.5,
                }}
              >
                {popover.entries.map((entry, i) => (
                  <Box key={getEntryKey(entry, i)}>
                    {renderEntry(entry, { inPopover: true })}
                  </Box>
                ))}
              </Box>
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

export default MonthCalendar;
